import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { useHasPermission } from "./session-context"
import { PlusIcon } from "./components/icons"
import { StatusTabs } from "./components/status-tabs"
import { ContentPagination } from "./content-components"
import { EMPTY_SCHOOL_FORM, SchoolFormModal, type SchoolFormValues } from "./components/school-form-modal"
import { SchoolsPageSkeleton } from "./components/skeleton"
import { toast } from "sonner"

type SchoolStatus = "application" | "active" | "inactive"
const PAGE_SIZE = 10

interface SchoolListItem {
  id: string
  code: string
  name: string
  city: string
  state: string
  enrollment: number
  childrenCount: number
  contactName: string
  contactTitle: string | null
  since: number | null
  status: SchoolStatus
}

interface StatusCounts {
  all: number
  active: number
  application: number
  inactive: number
}

export function SchoolsPage() {
  const navigate = useNavigate()
  const canView = useHasPermission("schools")
  const canMutate = useHasPermission("schools_mutate")
  const [schools, setSchools] = useState<SchoolListItem[]>([])
  const [counts, setCounts] = useState<StatusCounts>({ all: 0, active: 0, application: 0, inactive: 0 })
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<"active" | "application">("active")
  const [showAddModal, setShowAddModal] = useState(false)

  const fetchSchools = async () => {
    try {
      const query: { status?: SchoolStatus; limit: string; offset: string } = {
        limit: String(PAGE_SIZE),
        offset: String((page - 1) * PAGE_SIZE),
      }
      query.status = activeTab

      const res = await client.api.admin.schools.$get({ query })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        setSchools(body.data.items as SchoolListItem[])
        setCounts(body.data.counts)
        setTotal(body.data.total)
      }
    } catch {
      setError("Failed to load schools")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (canView) {
      void fetchSchools()
    } else {
      setLoading(false)
    }
  }, [activeTab, canView, page])

  useEffect(() => {
    setPage(1)
  }, [activeTab])

  if (!canView) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Schools</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to view schools.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return <SchoolsPageSkeleton />
  }

  const tabs: { key: "active" | "application"; label: string; count: number }[] = [
    { key: "active", label: "Active partners", count: counts.active },
    { key: "application", label: "Applications", count: counts.application },
  ]

  const addSchool = async (values: SchoolFormValues) => {
    try {
      const res = await client.api.admin.schools.$post({
        json: {
          name: values.name.trim(),
          address: values.address.trim() || undefined,
          city: values.city.trim(),
          state: values.state.trim(),
          lga: values.lga.trim() || undefined,
          enrollment: values.enrollment ? Number.parseInt(values.enrollment, 10) : 0,
          type: values.type,
          contactName: values.contactName.trim(),
          contactTitle: values.contactTitle.trim() || undefined,
          contactPhone: values.contactPhone.trim() || undefined,
          contactEmail: values.contactEmail.trim() || undefined,
          notes: values.notes.trim() || undefined,
        },
      })

      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("School added successfully")
      setShowAddModal(false)
      void fetchSchools()
    } catch {
      toast.error("Failed to add school")
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-4xl">Schools</h1>
          <p className="mt-1 text-sm text-ink/60">
            {counts.active} active partners · {counts.application} applications.
          </p>
        </div>
        {canMutate && (
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-forest-950 px-5 text-sm font-medium text-white transition-colors hover:bg-forest-900"
          >
            <PlusIcon className="h-4 w-4" />
            Add School
          </button>
        )}
      </div>

      <StatusTabs
        tabs={tabs}
        activeKey={activeTab}
        onChange={setActiveTab}
        ariaLabel="School status"
      />

      {/* Table */}
      {error ? (
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-600">{error}</p>
        </div>
      ) : schools.length === 0 ? (
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">
            {activeTab === "active" ? "No active partner schools" : "No school applications"}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-[10px] border border-gold-500 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gold-500/50 bg-cream-50">
                  <th className="px-5 py-4 text-left text-sm font-bold text-ink">
                    School
                  </th>
                  <th className="px-5 py-4 text-left text-sm font-bold text-ink">
                    Location
                  </th>
                  <th className="px-5 py-4 text-left text-sm font-bold text-ink">
                    Enrollment
                  </th>
                  <th className="px-5 py-4 text-left text-sm font-bold text-ink">
                    Our children
                  </th>
                  <th className="px-5 py-4 text-left text-sm font-bold text-ink">
                    Contact
                  </th>
                  <th className="px-5 py-4 text-left text-sm font-bold text-ink">
                    Since
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10">
                {schools.map((school) => (
                  <tr
                    key={school.id}
                    onClick={() => navigate(`/admin/schools/${school.id}`)}
                    className="cursor-pointer hover:bg-cream-50"
                  >
                    <td className="px-5 py-4 text-sm font-medium text-ink">{school.name}</td>
                    <td className="px-5 py-4 text-sm text-ink/70">
                      {school.city}, {school.state}
                    </td>
                    <td className="px-5 py-4 text-sm text-ink/70">{school.enrollment}</td>
                    <td className="px-5 py-4 text-sm text-ink/70">{school.childrenCount}</td>
                    <td className="px-5 py-4">
                      <div>
                        <p className="text-sm font-medium text-ink">{school.contactName}</p>
                        {school.contactTitle && (
                          <p className="text-xs text-ink/50">{school.contactTitle}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-ink">{school.since ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ContentPagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} disabled={loading} />

      <SchoolFormModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={addSchool}
        initialValues={EMPTY_SCHOOL_FORM}
      />
    </div>
  )
}
